"use client";

import type { AccordionCarouselBlock } from "@mw/shared/payload-types";
import { getImageProps, getVideoUrl, isEntity } from "@/app/_utilities/payload";
import Image from "next/image";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as m from "motion/react-m";
import { AnimatePresence, MotionConfig, useInView } from "motion/react";
import { useDebounce } from "use-debounce";
import { pxToRem } from "@ui/lib/utils";
import clsx from "clsx";
import { sizes } from "@ui/lib/sizes";
import { useTranslations } from "next-intl";
import { useHover } from "react-aria";

type Slide = NonNullable<AccordionCarouselBlock["slides"]>[number];
type ConversationMessages = NonNullable<NonNullable<Slide["overlay"]["conversation"]>["messages"]>;
type ImageAsset = Slide["asset"]["image"];
type VideoAsset = Slide["asset"]["video"];

export function BlockAccordionCarousel({ label, slides }: AccordionCarouselBlock) {
  const t = useTranslations("ClientKeys.BlockAccordionCarousel");
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [debouncedActiveIndex] = useDebounce(activeIndex, 400);
  const containerMetricsRef = useRef<{ scrollWidth: number; clientWidth: number } | null>(null);

  const checkActiveIndex = useCallback(function checkActiveIndex() {
    const container = containerRef.current;
    const metrics = containerMetricsRef.current;
    if (!container || !metrics) {
      return;
    }
    const scrollLeft = container.scrollLeft;
    const scrollWidth = metrics.scrollWidth;
    const clientWidth = metrics.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    const progress = scrollLeft / maxScroll;
    const newActiveIndex = Math.max(0, Math.min(3, Math.round(progress * 3)));
    setActiveIndex(newActiveIndex);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function onResize() {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      containerMetricsRef.current = { scrollWidth, clientWidth };
      checkActiveIndex();
    }

    onResize();

    const observer = new window.ResizeObserver(onResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [checkActiveIndex]);

  if (!slides || slides.length !== 4) {
    return null;
  }

  return (
    <MotionConfig reducedMotion='user'>
      <div
        ref={containerRef}
        aria-roledescription='carousel'
        aria-label={label}
        className='tw-flex lg:tw-h-[37.5rem] tw-gap-x-3 tw-overflow-x-auto lg:tw-overflow-visible tw-snap-x tw-snap-mandatory tw-scrollbar-none -tw-mx-full-bleed lg:tw-mx-0 tw-px-full-bleed lg:tw-px-0 tw-scroll-px-full-bleed lg:tw-scroll-px-0 tw-@container'
        onScroll={() => {
          checkActiveIndex();
        }}
      >
        {slides.map((slide, index) => (
          <Slide
            key={index}
            slide={slide}
            index={index}
            isActive={activeIndex === index && debouncedActiveIndex === index}
            activeIndex={activeIndex}
            hoverIndex={hoverIndex}
            ariaLabel={t("slide_label", { number: index + 1, total: slides.length })}
            onSelect={() => {
              setActiveIndex(index);
              setHoverIndex(null);
            }}
            onHoverStart={() => {
              setHoverIndex(index);
            }}
            onHoverEnd={() => {
              setHoverIndex(null);
            }}
          />
        ))}
      </div>
    </MotionConfig>
  );
}

function SlideImage({ imageAsset }: { imageAsset: ImageAsset }) {
  const image = imageAsset?.image;
  if (!image || !isEntity(image)) {
    return null;
  }
  const imageProps = getImageProps(image, { placeholder: true, fill: true });
  if (!imageProps) {
    return null;
  }
  return <Image {...imageProps} sizes={sizes(pxToRem(760))} objectFit='cover' />;
}

function SlideVideo({ videoAsset, isActive }: { videoAsset: VideoAsset; isActive: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [playing, setPlaying] = useState(false);
  const inView = useInView(videoRef);
  const [wasActive, setWasActive] = useState(false);

  if (isActive && !wasActive) {
    setWasActive(true);
  }

  const onCanPlayThrough = useCallback(() => {
    setCanPlayThrough(true);
  }, []);

  useEffect(() => {
    if (isActive && videoRef.current && inView) {
      if (canPlayThrough) {
        videoRef.current.play();
      }
    } else {
      videoRef.current?.pause();
    }
  }, [isActive, canPlayThrough, inView]);

  const url = getVideoUrl(videoAsset?.video);
  if (!url) {
    return null;
  }

  const src = wasActive ? url : undefined;

  const image = videoAsset?.thumbnail;
  const imageProps = getImageProps(image, { placeholder: true, fill: true });
  if (!imageProps) {
    return null;
  }

  return (
    <div className='tw-absolute tw-inset-0'>
      <Image {...imageProps} sizes={sizes(pxToRem(760))} objectFit='cover' />
      <div
        className={clsx(
          "tw-absolute tw-inset-0 tw-transition-opacity tw-duration-300",
          isActive && playing ? "" : "tw-opacity-0",
        )}
      >
        <video
          ref={videoRef}
          onCanPlayThrough={onCanPlayThrough}
          onPlay={() => setPlaying(true)}
          loop
          muted
          playsInline
          src={src}
          className='tw-absolute tw-inset-0 tw-size-full tw-object-cover'
        />
      </div>
    </div>
  );
}

function SlideAsset({ slide, isActive }: { slide: Slide; isActive: boolean }) {
  const { asset } = slide;

  if (asset.template === "image") {
    return <SlideImage imageAsset={asset.image} />;
  }

  if (asset.template === "video") {
    return <SlideVideo videoAsset={asset.video} isActive={isActive} />;
  }

  return null;
}

type SlideSizes = {
  xs: string;
  sm: string;
  md: string;
  lg: string;
};

function getBaseSlideWidth(index: number, activeIndex: number, slideSizes: SlideSizes) {
  return [
    ["var(--active-width)", slideSizes.md, slideSizes.sm, slideSizes.xs],
    [slideSizes.lg, "var(--active-width)", slideSizes.md, slideSizes.sm],
    [slideSizes.sm, slideSizes.md, "var(--active-width)", slideSizes.lg],
    [slideSizes.xs, slideSizes.sm, slideSizes.md, "var(--active-width)"],
  ][index][activeIndex];
}

function getSlideWidth({
  index,
  activeIndex,
  hoverIndex,
  slideSizes,
  hoverExpansion,
}: {
  index: number;
  activeIndex: number;
  hoverIndex: number | null;
  slideSizes: SlideSizes;
  hoverExpansion: string;
}) {
  const baseWidth = getBaseSlideWidth(index, activeIndex, slideSizes);
  const isHoveringInactive = hoverIndex !== null && hoverIndex !== activeIndex;

  if (!isHoveringInactive) {
    return baseWidth;
  }

  if (index === hoverIndex) {
    return `calc(${baseWidth} + ${hoverExpansion})`;
  }

  const beforeCount = hoverIndex;
  const afterCount = 3 - hoverIndex;
  const shrinkTotal = beforeCount === 0 || afterCount === 0 ? hoverExpansion : `calc(${hoverExpansion} / 2)`;

  if (index < hoverIndex) {
    return `calc(${baseWidth} - ${shrinkTotal} / ${beforeCount})`;
  }

  return `calc(${baseWidth} - ${shrinkTotal} / ${afterCount})`;
}

function Slide({
  slide,
  index,
  onSelect,
  isActive,
  activeIndex,
  ariaLabel,
  hoverIndex,
  onHoverStart,
  onHoverEnd,
}: {
  slide: Slide;
  index: number;
  isActive: boolean;
  onSelect?: () => void;
  activeIndex: number;
  ariaLabel: string;
  hoverIndex: number | null;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}) {
  const id = useId();
  const gap = pxToRem(12);
  const hoverExpansion = pxToRem(8);
  const slideSizes: SlideSizes = {
    xs: pxToRem(64),
    sm: pxToRem(96),
    md: pxToRem(142),
    lg: pxToRem(220),
  };
  const width = getSlideWidth({
    index,
    activeIndex,
    hoverIndex,
    slideSizes,
    hoverExpansion,
  });
  const { hoverProps } = useHover({
    onHoverStart() {
      if (!isActive) {
        onHoverStart?.();
      }
    },
    onHoverEnd() {
      onHoverEnd?.();
    },
  });

  return (
    <div
      className='tw-relative tw-isolate tw-flex-none tw-aspect-square sm:tw-aspect-[760/600] lg:tw-aspect-auto max-lg:!tw-w-[min(90%,47.5rem)] tw-snap-start'
      style={
        {
          "--active-width": `calc(100cqw - (${gap} * 3) - ${slideSizes.lg} - ${slideSizes.sm} - ${slideSizes.xs})`,
          width: `calc(${width})`,
        } as React.CSSProperties
      }
    >
      <button
        type='button'
        className='tw-group tw-absolute tw-z-50 tw-inset-y-0 -tw-inset-x-1.5 tw-hidden lg:tw-block tw-outline-none'
        inert={isActive}
        onClick={onSelect}
        {...hoverProps}
        aria-label={ariaLabel}
        aria-controls={`${id}-content`}
        aria-expanded={isActive}
        id={`${id}-button`}
      >
        <div className='tw-absolute tw-inset-y-0 tw-inset-x-1.5 tw-rounded-4xl group-focus-visible:tw-outline-focus' />
      </button>

      <m.div layout className='tw-absolute tw-inset-0 tw-bg-gray-200 tw-overflow-hidden' style={{ borderRadius: 32 }}>
        <m.div
          layout
          role='group'
          id={`${id}-content`}
          aria-labelledby={`${id}-button`}
          inert={!isActive}
          className='tw-absolute tw-inset-y-0 tw-left-0 tw-w-full lg:tw-w-[var(--active-width)] lg:tw-left-[calc(50%-var(--active-width)/2)]'
        >
          <div className='tw-absolute tw-inset-0 lg:-tw-inset-x-4'>
            <SlideAsset slide={slide} isActive={isActive} />
          </div>
          <SlideOverlay slide={slide} isActive={isActive} />
        </m.div>
      </m.div>
    </div>
  );
}

function SlideOverlay({ slide, isActive }: { slide: Slide; isActive: boolean }) {
  if (slide.overlay.template === "conversation") {
    const messages = slide.overlay.conversation?.messages;
    if (!messages) {
      return null;
    }
    return <AnimatePresence initial={false}>{isActive && <ConversationOverlay messages={messages} />}</AnimatePresence>;
  }

  return null;
}

function ConversationOverlay(props: { messages: ConversationMessages }) {
  const [visibleMessages, setVisibleMessages] = useState<ConversationMessages>([]);
  const messages = useMemo(
    () => props.messages?.filter(message => message.message?.trim().length > 0),
    [props.messages],
  );

  useEffect(() => {
    function getInterval(index: number): number {
      const defaultInterval = 1000;
      const message = messages[index];
      if (!message) {
        return defaultInterval;
      }

      if (typeof message.delay !== "number") {
        return defaultInterval;
      }

      const elapsedBefore = Array.from({ length: index }, (_, previousIndex) => previousIndex).reduce(
        (sum, previousIndex) => sum + getInterval(previousIndex),
        0,
      );

      return Math.max(message.delay - elapsedBefore, 0);
    }

    let timer = window.setTimeout(() => {
      nextMessage(0);
    }, getInterval(0));

    function nextMessage(index: number) {
      setVisibleMessages(prev => [...prev, ...(messages[prev.length] ? [messages[prev.length]] : [])]);
      if (index < messages.length - 1) {
        timer = window.setTimeout(
          () => {
            nextMessage(index + 1);
          },
          getInterval(index + 1),
        );
      }
    }

    return () => {
      window.clearTimeout(timer);
    };
  }, [messages]);

  const [layoutKey, setLayoutKey] = useState(0);

  useLayoutEffect(() => {
    setLayoutKey(prev => (visibleMessages.length === 1 ? prev + 1 : prev));
  }, [visibleMessages.length]);

  if (!messages || messages.length === 0) {
    return null;
  }

  return (
    <m.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='tw-absolute tw-inset-0 tw-p-5 tw-flex tw-items-end tw-justify-end'
    >
      <m.div
        className='tw-max-w-80 tw-w-full'
        key={layoutKey}
        layout={visibleMessages.length > 0 ? "position" : undefined}
      >
        <AnimatePresence initial={true} mode='popLayout'>
          {visibleMessages.map((message, index) => (
            <m.div
              key={index}
              layout
              className={clsx(
                "tw-mt-2 tw-rounded-2xl tw-py-4 tw-px-3.5 tw-type-xs tw-text-pretty tw-w-fit",
                message.role === "agent"
                  ? "tw-text-black tw-bg-white"
                  : "tw-text-white tw-bg-white/25 tw-backdrop-blur-md tw-ml-auto tw-ring-1 tw-ring-inset tw-ring-white/10",
              )}
              style={{
                maxWidth: "calc(100% - 2rem)",
                boxShadow:
                  message.role === "agent" ? "0 0 1px rgb(0 0 0 / 0.4), 0 2px 4px rgb(0 0 0 / 0.04)" : undefined,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {message.message}
            </m.div>
          ))}
        </AnimatePresence>
      </m.div>
    </m.div>
  );
}
